<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        icon
        v-on="on"
      >
        <v-icon color="hrissecondary">
          mdi-plus-circle
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Employee Disciplines"
      text="Create Employee Disciplines"
      class="mb-0"
      :loading="loading"
    >
      <v-container v-if="dialog && !loading">
        <v-row
          no-gutters
          class="mt-2"
        >
          <v-col 
            cols="12"
          >
            <v-menu
              v-model="dateOfMisconductMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="dateOfMisconductDisplay"
                  v-validate="'required'"
                  outlined
                  label="Date of Misconduct"
                  :error-messages="errors.collect('dateOfMisconductDisplay')"
                  data-vv-name="dateOfMisconductDisplay"  
                  data-vv-as="date of misconduct"      
                  readonly
                  class="label-position"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="dateOfMisconduct"
                :max="today"
                @input="dateOfMisconductMenu = false"
                @change="loadTypeOfMisconducts(); typeOfMisconducts = []"
              />
            </v-menu>
          </v-col>
        </v-row>
        <v-row 
          v-if="dateOfMisconduct && typeOfMisconducts.length > 0"
          no-gutters
        >
          <v-col cols="12">
            <v-autocomplete
              v-model="disciplinaryCategory"
              v-validate="'required'"
              outlined
              :error-messages="errors.collect('disciplinaryCategory')"
              :items="disciplinaryCategories"
              data-vv-name="disciplinaryCategory"
              data-vv-as="disciplinary category"
              item-text="name"
              item-value="id"
              label="Disciplinary Category"
            />
          </v-col>

          <v-col 
            cols="12"
          >
            <v-autocomplete
              v-model="disciplinaryAction"
              v-validate="'required'"
              :error-messages="errors.collect('disciplinaryAction')"
              :items="disciplinaryActions"
              data-vv-name="disciplinaryAction"
              data-vv-as="disciplinary action"
              outlined
              item-text="name"
              item-value="id"
              label="Disciplinary Action"
            />
          </v-col>

          <v-col cols="12">
            <v-autocomplete
              v-model="typeOfMisconduct"
              v-validate="'required'"
              outlined
              :error-messages="errors.collect('typeOfMisconduct')"
              :items="typeOfMisconducts"
              data-vv-name="typeOfMisconduct"
              data-vv-as="type of misconduct"
              :item-text="item => '['+item.code+'] - '+ item.name"
              item-value="id"
              label="Type of Misconduct"
            >
              <template v-slot:selection="data">
                <span v-html="'['+data.item.code+'] - ' + data.item.name" />
              </template>
              
              <template v-slot:item="data">
                <v-list-item-content style="max-width:400px">
                  <p
                    class="font-weight-bold"
                    v-html="'['+ data.item.code +'] '+ data.item.name"
                  />
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col
            cols="12"
          >
            <v-menu
              v-model="dateOfActionTakenMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="dateOfActionTakenDisplay"
                  v-validate="'required'"
                  label="Date of Action Taken"
                  outlined
                  :error-messages="errors.collect('dateOfActionTakenDisplay')"
                  data-vv-name="dateOfActionTakenDisplay"  
                  data-vv-as="date of action taken"      
                  readonly
                  class="label-position"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="dateOfActionTaken"
                :min="dateOfMisconduct"
                @input="dateOfActionTakenMenu = false"
              />
            </v-menu>
          </v-col>
          
          <v-col cols="12">
            <v-textarea
              v-model="remark"
              outlined
              :rows="4"
              label="Remark"
            />
          </v-col>

          <v-col cols="12">
            <helper-file-input
              v-model="file" 
              :max-size-in-mb="10"
              :max-number="5"
              :multiple="true"
              data-vv-name="files"
              field-name="files"
              accept-type=".pdf"    
              extension="ext:pdf"      
            >
              <div 
                slot="activator"
                class="mb-2"
              >   
                <v-btn                    
                  :disabled="progressing"
                  class="mx-2"    
                >
                  Add file
                </v-btn>
                <span> 
                  {{ file ? '' : 'No file chosen (.pdf only)' }}
                </span>
                <div
                  v-if="file"
                  class="mt-4"
                >
                  <p
                    v-for="selectedFile in file.selectedFiles"
                    :key="selectedFile.name"
                    class="mb-0"
                  >
                    <span class="font-weight-bold">{{ selectedFile.name }}</span>
                  </p>
                </div>
              </div>
            </helper-file-input>
            <span class="red--text text--lighten-1">{{ errors.first('files') }}</span>
          </v-col> 
        </v-row>
        
        <v-row v-if="!loading && noRecord && dateOfMisconduct">
          <v-col
            cols="12"
            class="px-6 pb-2 mt-n3"
          >
            <v-icon
              class="pr-1"
              color="red"
            >
              mdi-information-outline
            </v-icon>
            <span
              class="red--text"
            >
              No type of misconducts record found for the date selected
            </span>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions v-if="!loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          :disabled="noRecord || !disciplinaryCategory || !disciplinaryAction || !typeOfMisconduct || !dateOfMisconduct || !dateOfActionTaken"
          color="hrissecondary"    
          class="white--text px-10"        
          @click="submitForm"
        >
          Create
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    components: {
      HelperFileInput: () => import('@/components/helper/FileInput'),
    },
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employeeUid: {
        type: String, 
        required: true,
      },
    },
    data () {
      return {
        progressing: false,
        loading: false,
        dialog: false,
        remark: null,
        file: null,
        disciplinaryActions: [],
        disciplinaryCategories: [],
        typeOfMisconducts: [],
        typeOfMisconduct: null,
        disciplinaryAction: null,
        disciplinaryCategory: null,
        dateOfActionTakenMenu: false,
        dateOfActionTaken: null,
        dateOfMisconductMenu: false,
        dateOfMisconduct: null,
        jobGroup: null,
        jobGroupPolicies: [],
        group: null,
        noRecord: false,
        today: new Date().toISOString().slice(0, 10), 
      }
    },

    computed: {
      dateOfActionTakenDisplay () {
        return this.dateOfActionTaken ? this.$moment(this.dateOfActionTaken, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      dateOfMisconductDisplay () {
        return this.dateOfMisconduct ? this.$moment(this.dateOfMisconduct, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
    },
    watch: {
      dialog (value) {
        if (!value) return this.$resetData()

        this.loading = true
        Promise.all([
          this.$api.disciplinaryActions().list(),
          this.$api.disciplinaryCategories().list(),
          this.$api.jobGroupPolicies().list(),
          this.$api.employeePositions().show(this.employeeUid),
        ])
          .then(([disciplinaryAction, disciplinaryCategory, jobGroupPolicy, employeePosition]) => {
            this.disciplinaryActions = disciplinaryAction.data
            this.disciplinaryCategories = disciplinaryCategory.data
            this.jobGroupPolicies = jobGroupPolicy.data
            this.group = employeePosition.data.position.job_grade.job_group.job_group_policy_id

            this.loading = false
          })
      },
    },
    methods: {
      loadTypeOfMisconducts () {
        this.$api.typeOfMisconducts().list({ date_of_misconduct: this.dateOfMisconduct, employee_uid: this.employeeUid })
          .then(res => {
            this.typeOfMisconducts = res.data

            if (this.typeOfMisconducts.length <= 0) {
              this.noRecord = true
            } else {
              this.noRecord = false
            }
          })
          .catch(error => {
            if (error.response.status === 404) {
              this.$snack({ 
                color: 'error',
                message: error.response.data.message, 
              })
            }
          })
      },

      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.employeeDisciplineCases().create(this.employeeUid, this.prepareFormData())
              .then(response => {
                if (response.status >= 200 && response.status < 300) {
                  this.$emit('created')
                  this.$snack({ message: response.data.status })

                  this.dialog = false
                }
              })
              .catch(() => {})
              .then(() => {
                this.progressing = false
              })
          })
      },
  
      prepareFormData () {
        if (this.file) {
          this.file.formData.set('disciplinary_category_id', this.disciplinaryCategory)
          this.file.formData.set('type_of_misconduct_id', this.typeOfMisconduct)
          this.file.formData.set('date_of_misconduct', this.dateOfMisconduct)
          this.file.formData.set('disciplinary_action_id', this.disciplinaryAction)
          this.file.formData.set('date_of_action_taken', this.dateOfActionTaken)
          this.file.formData.set('remarks', this.remark)
   
          return this.file.formData
        }

        let formData = new FormData()

        formData.append('disciplinary_category_id', this.disciplinaryCategory)
        formData.append('type_of_misconduct_id', this.typeOfMisconduct)
        formData.append('date_of_misconduct', this.dateOfMisconduct)
        formData.append('disciplinary_action_id', this.disciplinaryAction)
        formData.append('date_of_action_taken', this.dateOfActionTaken)
        formData.append('remarks', this.remark)

        return formData
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-3px;
  background:white;
  padding-left:4px;
  padding-right:4px
}
</style>
